/**
 * Core Module Integration Setup
 *
 * Integrates module-core's unified Apollo Client with the main app's
 * Apollo Client and auth system, including full token refresh support.
 *
 * IMPORTANT: This should be called ONCE during app initialization,
 * before any modules that use GraphQL are loaded.
 *
 * @example
 * // In _app.tsx or a provider component
 * import { setupCoreIntegration } from '@kidslabs/module-core';
 *
 * useEffect(() => {
 *   setupCoreIntegration(apolloClient, authBridge);
 * }, [apolloClient]);
 */

import {
    setUnifiedClient,
    setAuthTokenProvider,
    setCustomHeadersGetter,
    setOnAuthError,
    setCoreClientCacheConfig,
    type AuthTokenProvider,
    type RefreshedToken,
} from './api/unified-client';
import { authStore$ } from './auth';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { InMemoryCacheConfig } from '@apollo/client/cache/inmemory/types';

/**
 * Auth bridge interface between the app and core module
 *
 * The app provides platform-specific implementations of these callbacks,
 * and the core module uses them for authentication, token refresh, and headers.
 */
export interface AppAuthBridge {
    /**
     * Get the current access token from app's storage
     * This should be the SAME function used by app's Apollo Client
     */
    getAccessToken: () => Promise<string | null>;

    /**
     * Set the access token (after refresh, login, etc.)
     */
    setAccessToken?: (token: string) => Promise<void>;

    /**
     * Clear all auth data (on logout or token refresh failure)
     */
    clearAuth?: () => Promise<void>;

    /**
     * Get custom headers for requests (store code, cache ID, API key, etc.)
     */
    getCustomHeaders?: () => Record<string, string>;

    /**
     * Handle auth errors (redirect to login, show notification, etc.)
     */
    onAuthError?: (error: Error) => void;

    /**
     * Get the refresh token for token renewal
     * Called when an auth error occurs and token needs to be refreshed
     *
     * For Next.js: typically reads from NextAuth session
     * For other apps: reads from storage/state
     */
    getRefreshToken?: () => Promise<string | null>;

    /**
     * Called with the full refreshed token response
     * Use this to persist both access_token and refresh_token
     */
    onTokenRefreshed?: (token: RefreshedToken) => void;

    /**
     * Called when a cart permission error is received from the server
     * (e.g., "The current user cannot perform operations on cart")
     *
     * Use this to clear all cart-related local stores (IndexedDB, etc.)
     * so the user gets a fresh cart on next operation.
     */
    onCartError?: (errorMessage: string) => void;

    /**
     * Server-side refresh endpoint URL (e.g., '/api/v2/auth/refresh-token').
     * When set, token refresh is done via this endpoint instead of a direct
     * GraphQL mutation. Required for Identity Auth where refresh tokens
     * are stored server-side in Redis.
     */
    serverRefreshEndpoint?: string;

    /**
     * InMemoryCache configuration (typePolicies, key fields, etc.)
     *
     * Passed so the fallback client created by getUnifiedClient()
     * uses the same cache behaviour as the app's own Apollo Client.
     */
    cacheConfig?: InMemoryCacheConfig;
}

/**
 * Setup core module integration with app
 *
 * Connects the app's Apollo Client and auth system with module-core,
 * ensuring all modules use the same client, tokens, and refresh logic.
 *
 * @param apolloClient - The app's Apollo Client instance
 * @param authBridge - Bridge to app's auth system
 */
export function setupCoreIntegration(
    apolloClient: ApolloClient<NormalizedCacheObject>,
    authBridge: AppAuthBridge
): void {
    // 1. Set the unified Apollo Client
    setUnifiedClient(apolloClient);

    // 2. Set auth token provider with full refresh support
    const tokenProvider: AuthTokenProvider = {
        getToken: async () => {
            const token = await authBridge.getAccessToken();
            // Sync to authStore$ for Legend-State reactivity
            if (token) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                authStore$.customerToken?.set({ access_token: token } as any);
                authStore$.isAuthenticated?.set(true);
            }
            return token;
        },
        setToken: authBridge.setAccessToken
            ? async (token: string) => {
                  await authBridge.setAccessToken!(token);
                  // Sync to authStore$
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  authStore$.customerToken?.set({ access_token: token } as any);
                  authStore$.isAuthenticated?.set(true);
              }
            : undefined,
        clearToken: authBridge.clearAuth
            ? async () => {
                  await authBridge.clearAuth!();
                  // Clear authStore$
                  authStore$.customerToken?.set(undefined);
                  authStore$.customerProfile?.set(undefined);
                  authStore$.isAuthenticated?.set(false);
              }
            : undefined,
        // Token refresh support
        getRefreshToken: authBridge.getRefreshToken,
        onTokenRefreshed: authBridge.onTokenRefreshed,
        // Cart error support
        onCartError: authBridge.onCartError,
        // Server-side refresh endpoint (for Identity Auth)
        serverRefreshEndpoint: authBridge.serverRefreshEndpoint,
    };
    setAuthTokenProvider(tokenProvider);

    // 3. Set custom headers getter (if provided)
    if (authBridge.getCustomHeaders) {
        setCustomHeadersGetter(authBridge.getCustomHeaders);
    }

    // 4. Set auth error handler (if provided)
    if (authBridge.onAuthError) {
        setOnAuthError(authBridge.onAuthError);
    }

    // 5. Register cache config so the fallback client uses proper typePolicies
    if (authBridge.cacheConfig) {
        setCoreClientCacheConfig(authBridge.cacheConfig);
    }

    console.log('[CoreIntegration] Module-core integrated with app successfully');
}

/**
 * React hook version for components that need to setup integration
 * Use this if you need to setup in a React context
 */
export { setupCoreIntegration as useCoreIntegration };
